/* ==========================================================================
   CLYDE CLONE — cb-theme.js
   Header condense-on-scroll, drawer nav, hero slideshow.
   Loaded with `defer` so the DOM is ready when this runs.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------------
     1. Header — add .is-stuck once the page scrolls past the threshold
     --------------------------------------------------------------------- */
  function initHeader() {
    var header = document.querySelector('[data-cb-header]');
    if (!header) return;

    // Two thresholds, not one. Condensing the header removes the nav row,
    // which shortens the document and can nudge the scroll position back
    // across a single threshold — the class then toggles on and off every
    // frame and the header appears to flicker. A dead zone between the two
    // values makes that oscillation impossible.
    // Opt out entirely: never add the class, so nothing about the header
    // changes on scroll and there is no layout shift to chase.
    if (header.dataset.cbCondense === 'false') return;

    var stickAt = parseInt(header.dataset.cbStickAfter || '120', 10);
    var releaseAt = Math.max(0, stickAt - 90);

    var stuck = false;
    var ticking = false;

    // The nav row collapses by height, and height:auto cannot be animated,
    // so measure it once and hand the pixel value to CSS.
    var nav = header.querySelector('.cb-nav');

    function measureNav() {
      if (!nav) return;
      var wasStuck = header.classList.contains('is-stuck');
      if (wasStuck) header.classList.remove('is-stuck');
      header.style.removeProperty('--cb-nav-h');
      var h = nav.offsetHeight;
      if (h) header.style.setProperty('--cb-nav-h', h + 'px');
      if (wasStuck) header.classList.add('is-stuck');
    }

    measureNav();
    window.addEventListener('resize', measureNav);

    // Homepage-only: hand the header's own rendered height to CSS so the
    // transparent overlay header (see custom.css, "TRANSPARENT OVERLAY
    // HEADER") can pull the hero up underneath it by exactly that amount.
    // Scoped to .template-index so this does nothing on pages without a
    // hero directly under the header.
    function measureHeaderOverlay() {
      if (!document.body.classList.contains('template-index')) return;
      var wrapper = header.closest('.cb-header-wrapper');
      if (!wrapper) return;
      wrapper.style.setProperty('--cb-header-h', header.offsetHeight + 'px');
    }

    measureHeaderOverlay();
    window.addEventListener('resize', measureHeaderOverlay);

    function update() {
      ticking = false;
      var y = window.scrollY;

      if (!stuck && y > stickAt) {
        stuck = true;
        header.classList.add('is-stuck');
      } else if (stuck && y < releaseAt) {
        stuck = false;
        header.classList.remove('is-stuck');
      }
    }

    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );

    update();
  }

  /* ---------------------------------------------------------------------
     2. Drawer navigation
     --------------------------------------------------------------------- */
  function initDrawer() {
    var drawer = document.querySelector('[data-cb-drawer]');
    var overlay = document.querySelector('[data-cb-drawer-overlay]');
    if (!drawer || !overlay) return;

    // Move both to <body>. They are authored inside the header section, and
    // any ancestor with a transform, filter or will-change would become the
    // containing block for their `position: fixed` and clip them to that
    // element. Sitting directly on <body> makes them immune to whatever the
    // header is doing.
    if (drawer.parentNode !== document.body) document.body.appendChild(drawer);
    if (overlay.parentNode !== document.body) document.body.appendChild(overlay);

    var openers = document.querySelectorAll('[data-cb-drawer-open]');
    var closers = document.querySelectorAll('[data-cb-drawer-close]');
    var lastFocused = null;

    function open() {
      lastFocused = document.activeElement;
      drawer.hidden = false;
      overlay.hidden = false;
      // Next frame so the transition actually runs.
      window.requestAnimationFrame(function () {
        drawer.classList.add('is-open');
        overlay.classList.add('is-open');
      });
      document.body.style.overflow = 'hidden';
      var first = drawer.querySelector('[data-cb-drawer-close]');
      if (first) first.focus();
    }

    function close() {
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-open');
      document.body.style.overflow = '';
      window.setTimeout(function () {
        drawer.hidden = true;
        overlay.hidden = true;
      }, 280);
      if (lastFocused) lastFocused.focus();
    }

    openers.forEach(function (btn) { btn.addEventListener('click', open); });
    closers.forEach(function (btn) { btn.addEventListener('click', close); });
    overlay.addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !drawer.hidden) close();
    });
  }

  /* ---------------------------------------------------------------------
     3. Hero slideshow
     --------------------------------------------------------------------- */
  function initHero() {
    document.querySelectorAll('[data-cb-hero]').forEach(function (hero) {
      var slides = Array.prototype.slice.call(
        hero.querySelectorAll('[data-cb-slide]')
      );
      var dots = Array.prototype.slice.call(
        hero.querySelectorAll('[data-cb-dot]')
      );
      if (slides.length < 2) return;

      var index = 0;
      var timer = null;
      var interval = parseInt(hero.dataset.cbInterval || '5000', 10);
      var autoplay = hero.dataset.cbAutoplay === 'true';
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function show(next) {
        index = (next + slides.length) % slides.length;
        slides.forEach(function (slide, i) {
          slide.classList.toggle('is-active', i === index);
          slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
        });
        dots.forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === index);
          dot.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
      }

      function start() {
        if (!autoplay || reduced) return;
        stop();
        timer = window.setInterval(function () { show(index + 1); }, interval);
      }

      function stop() {
        if (timer) window.clearInterval(timer);
        timer = null;
      }

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          show(i);
          start();
        });
      });

      hero.addEventListener('mouseenter', stop);
      hero.addEventListener('mouseleave', start);
      hero.addEventListener('focusin', stop);

      show(0);
      start();
    });
  }


  /* ---------------------------------------------------------------------
     4. Product tabs
     --------------------------------------------------------------------- */
  function initTabs() {
    document.querySelectorAll('[data-cb-tabs]').forEach(function (root) {
      var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-cb-tab]'));
      var panels = Array.prototype.slice.call(root.querySelectorAll('[data-cb-panel]'));
      if (tabs.length < 2) return;

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var key = tab.dataset.cbTab;
          tabs.forEach(function (t) {
            var on = t.dataset.cbTab === key;
            t.classList.toggle('is-active', on);
            t.setAttribute('aria-selected', on ? 'true' : 'false');
          });
          panels.forEach(function (p) {
            p.hidden = p.dataset.cbPanel !== key;
          });
        });
      });
    });
  }

  /* ---------------------------------------------------------------------
     5. Flash countdown
     --------------------------------------------------------------------- */
  function initFlash() {
    document.querySelectorAll('[data-cb-flash]').forEach(function (root) {
      var raw = (root.dataset.cbEnds || '').trim();
      if (!raw) return;

      // "YYYY-MM-DD HH:MM" is not reliably parsed by Safari — normalise it.
      var iso = raw.replace(' ', 'T');
      if (iso.length === 16) iso += ':00';
      var end = new Date(iso).getTime();
      if (isNaN(end)) return;

      var clock = root.querySelector('[data-cb-clock]');
      var ended = root.querySelector('[data-cb-flash-ended]');
      var d = root.querySelector('[data-cb-days]');
      var h = root.querySelector('[data-cb-hours]');
      var m = root.querySelector('[data-cb-mins]');
      var s = root.querySelector('[data-cb-secs]');
      var timer = null;

      function pad(n) { return n < 10 ? '0' + n : String(n); }

      function tick() {
        var left = end - Date.now();
        if (left <= 0) {
          if (clock) clock.hidden = true;
          if (ended) ended.hidden = false;
          window.clearInterval(timer);
          return;
        }
        var sec = Math.floor(left / 1000);
        if (d) d.textContent = pad(Math.floor(sec / 86400));
        if (h) h.textContent = pad(Math.floor(sec / 3600) % 24);
        if (m) m.textContent = pad(Math.floor(sec / 60) % 60);
        if (s) s.textContent = pad(sec % 60);
      }

      tick();
      timer = window.setInterval(tick, 1000);
    });
  }

  /* ---------------------------------------------------------------------
     6. Copy discount code
     --------------------------------------------------------------------- */
  function initCopy() {
    document.querySelectorAll('[data-cb-copy]').forEach(function (btn) {
      var label = btn.querySelector('[data-cb-copy-label]');
      if (!label) return;
      var original = label.textContent;

      btn.addEventListener('click', function () {
        var code = btn.dataset.cbCopy;

        function done() {
          label.textContent = 'Copied: ' + code;
          window.setTimeout(function () { label.textContent = original; }, 2000);
        }

        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(code).then(done).catch(fallback);
        } else {
          fallback();
        }

        function fallback() {
          var ta = document.createElement('textarea');
          ta.value = code;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          try { document.execCommand('copy'); done(); } catch (e) { /* no-op */ }
          document.body.removeChild(ta);
        }
      });
    });
  }

  /* ---------------------------------------------------------------------
     7. Featured product — gallery, options, quantity
     --------------------------------------------------------------------- */
  function initFeatured() {
    document.querySelectorAll('[data-cb-featured]').forEach(function (root) {

      // Gallery
      var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-cb-thumb]'));
      var mains = Array.prototype.slice.call(root.querySelectorAll('[data-cb-main]'));

      function showImage(key) {
        thumbs.forEach(function (t) { t.classList.toggle('is-active', t.dataset.cbThumb === key); });
        mains.forEach(function (mn) { mn.classList.toggle('is-active', mn.dataset.cbMain === key); });
      }
      thumbs.forEach(function (t) {
        t.addEventListener('click', function () { showImage(t.dataset.cbThumb); });
      });

      // Quantity
      var qtyInput = root.querySelector('input[name="quantity"]');
      root.querySelectorAll('[data-cb-qty]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (!qtyInput) return;
          var step = parseInt(btn.dataset.cbQty, 10);
          var next = (parseInt(qtyInput.value, 10) || 1) + step;
          qtyInput.value = next < 1 ? 1 : next;
        });
      });

      // Options
      var dataEl = root.querySelector('[data-cb-variants]');
      if (!dataEl) return;

      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
      if (!variants || !variants.length) return;

      var idField = root.querySelector('[data-cb-variant-id]');
      var priceEl = root.querySelector('[data-cb-price]');
      var atc = root.querySelector('[data-cb-atc]');
      var groups = Array.prototype.slice.call(root.querySelectorAll('[data-cb-option-index]'));

      function money(cents) {
        // Read the currency symbol already rendered on the page so we don't
        // guess a format the shop isn't using.
        var sample = priceEl ? priceEl.textContent.trim() : '';
        var symbol = sample.replace(/[\d.,\s]/g, '') || '';
        return symbol + (cents / 100).toFixed(2);
      }

      function currentSelection() {
        return groups.map(function (g) {
          var active = g.querySelector('.cb-fp__swatch.is-active');
          return active ? active.dataset.cbValue : null;
        });
      }

      function update() {
        var chosen = currentSelection();
        var match = variants.find(function (v) {
          return chosen.every(function (val, i) { return v.options[i] === val; });
        });

        if (!match) {
          if (atc) { atc.disabled = true; atc.textContent = 'Unavailable'; }
          return;
        }

        if (idField) idField.value = match.id;
        if (priceEl) priceEl.textContent = money(match.price);
        if (atc) {
          atc.disabled = !match.available;
          atc.textContent = match.available ? atc.dataset.cbLabel : 'Sold out';
        }

        // Swap the gallery to this variant's image when it has one.
        if (match.featured_image && match.featured_image.src) {
          var target = null;
          mains.forEach(function (mn) {
            var img = mn.querySelector('img');
            if (!img || target !== null) return;
            var base = match.featured_image.src.split('?')[0].split('/').pop();
            if (img.src.indexOf(base) !== -1) target = mn.dataset.cbMain;
          });
          if (target !== null) showImage(target);
        }
      }

      if (atc) atc.dataset.cbLabel = atc.textContent.trim();

      groups.forEach(function (g) {
        g.querySelectorAll('.cb-fp__swatch').forEach(function (btn) {
          btn.addEventListener('click', function () {
            g.querySelectorAll('.cb-fp__swatch').forEach(function (b) {
              b.classList.remove('is-active');
            });
            btn.classList.add('is-active');
            var out = g.querySelector('[data-cb-option-selected]');
            if (out) out.textContent = btn.dataset.cbValue;
            update();
          });
        });
      });

      update();
    });
  }

  /* --------------------------------------------------------------------- */
  /* ---------------------------------------------------------------------
     Collage videos — play only while on screen
     --------------------------------------------------------------------- */
  function initCollageVideo() {
    var vids = [].slice.call(document.querySelectorAll('.cb-collage__slot video'));
    if (!vids.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      vids.forEach(function (v) { v.pause(); v.removeAttribute('autoplay'); });
      return;
    }

    vids.forEach(function (v) {
      v.muted = true;                 // autoplay is blocked without this
      v.setAttribute('muted', '');
    });

    if (!('IntersectionObserver' in window)) return;

    // Several clips playing at once is the quickest way to make a homepage
    // feel slow. Nothing downloads or plays until it is visible, and it all
    // stops again once it scrolls away.
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var v = e.target;
        if (e.isIntersecting) {
          if (v.preload === 'none') v.preload = 'auto';
          var p = v.play();
          if (p && p.catch) p.catch(function () { /* browser declined */ });
        } else if (!v.paused) {
          v.pause();
        }
      });
    }, { rootMargin: '100px', threshold: 0.25 });

    vids.forEach(function (v) { io.observe(v); });
  }

  /* ---------------------------------------------------------------------
     Video reel — arrows, plus play only what is on screen
     --------------------------------------------------------------------- */
  function initVideoReel() {
    document.querySelectorAll('[data-cb-reel]').forEach(function (reel) {
      if (reel.dataset.cbReady === '1') return;
      reel.dataset.cbReady = '1';

      var track = reel.querySelector('[data-cb-reel-track]');
      if (!track) return;
      var prev = reel.querySelector('[data-cb-reel-prev]');
      var next = reel.querySelector('[data-cb-reel-next]');

      /* --- Arrows --- */
      function step() {
        var cell = track.querySelector('.cb-reel__cell');
        if (!cell) return track.clientWidth;
        var cs = window.getComputedStyle(track);
        var gap = parseFloat(cs.columnGap || cs.gap || 0) || 0;
        return cell.getBoundingClientRect().width + gap;
      }
      function syncArrows() {
        if (!prev || !next) return;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
      }
      if (prev) prev.addEventListener('click', function () {
        track.scrollBy({ left: -step() * 2, behavior: 'smooth' });
      });
      if (next) next.addEventListener('click', function () {
        track.scrollBy({ left: step() * 2, behavior: 'smooth' });
      });
      track.addEventListener('scroll', syncArrows, { passive: true });
      window.addEventListener('resize', syncArrows);
      syncArrows();

      /* --- Playback: on demand only --- */
      var cards = [].slice.call(reel.querySelectorAll('[data-cb-reel-card]'));
      var vids = [].slice.call(reel.querySelectorAll('video'));
      if (!vids.length) return;

      // Belt and braces: the markup already sets muted, and nothing in the
      // theme ever unsets it.
      vids.forEach(function (v) {
        v.muted = true;
        v.setAttribute('muted', '');
      });

      function stop(card) {
        var v = card.querySelector('video');
        if (v) { v.pause(); v.currentTime = 0; }
        card.classList.remove('is-playing');
        var b = card.querySelector('[data-cb-reel-play]');
        if (b) b.setAttribute('aria-label', 'Play video');
      }

      function stopAll(except) {
        cards.forEach(function (c) { if (c !== except) stop(c); });
      }

      cards.forEach(function (card) {
        var btn = card.querySelector('[data-cb-reel-play]');
        if (!btn) return;

        btn.addEventListener('click', function (e) {
          // The whole card may be a link; this press is about the video.
          e.preventDefault();
          e.stopPropagation();

          var v = card.querySelector('video');
          if (!v) return;

          if (v.paused) {
            // preload was 'none', so this is the first time anything for
            // this clip is fetched.
            if (v.preload === 'none') v.preload = 'auto';
            stopAll(card);
            v.muted = true;
            var p = v.play();
            if (p && p.catch) p.catch(function () { card.classList.remove('is-playing'); });
            card.classList.add('is-playing');
            btn.setAttribute('aria-label', 'Pause video');
          } else {
            stop(card);
          }
        });
      });

      // A clip left running after the row is scrolled past would play on for
      // nothing, so stop everything once the section leaves the screen.
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          if (!entries[0].isIntersecting) stopAll(null);
        }, { threshold: 0 }).observe(reel);
      }
    });
  }

  /* ---------------------------------------------------------------------
     WhatsApp greeting bubble
     --------------------------------------------------------------------- */
  function initWhatsApp() {
    var wa = document.querySelector('[data-cb-wa]');
    if (!wa || wa.dataset.cbReady === '1') return;
    wa.dataset.cbReady = '1';

    var bubble = wa.querySelector('[data-cb-wa-bubble]');
    if (!bubble) return;

    // Dismissed once, gone for the rest of the visit. sessionStorage rather
    // than a cookie: it clears itself when the tab closes.
    var KEY = 'cb-wa-dismissed';
    try {
      if (window.sessionStorage.getItem(KEY) === '1') return;
    } catch (e) { /* private mode */ }

    var delay = parseInt(wa.dataset.cbWaDelay || '8000', 10);
    window.setTimeout(function () { bubble.hidden = false; }, delay);

    var x = wa.querySelector('[data-cb-wa-dismiss]');
    if (x) {
      x.addEventListener('click', function () {
        bubble.hidden = true;
        try { window.sessionStorage.setItem(KEY, '1'); } catch (e) { /* ignore */ }
      });
    }
  }

  function init() {
    initHeader();
    initDrawer();
    initHero();
    initTabs();
    initFlash();
    initCopy();
    initFeatured();
    initCollageVideo();
    initVideoReel();
    initWhatsApp();
  }

  init();

  // Re-run when a section is re-rendered in the theme customizer.
  document.addEventListener('shopify:section:load', init);
})();

/* ==========================================================================
   BATCH 2 — tabs, countdown, copy code, featured product
   ========================================================================== */

(function () {
  'use strict';

  /* --- Tabbed product rows --- */
  function initTabs() {
    document.querySelectorAll('[data-cb-tabs]').forEach(function (root) {
      var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-cb-tab]'));
      var panels = Array.prototype.slice.call(root.querySelectorAll('[data-cb-panel]'));
      if (!tabs.length) return;

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var idx = tab.dataset.cbTab;
          tabs.forEach(function (t) {
            var on = t.dataset.cbTab === idx;
            t.classList.toggle('is-active', on);
            t.setAttribute('aria-selected', on ? 'true' : 'false');
          });
          panels.forEach(function (p) {
            var on = p.dataset.cbPanel === idx;
            p.classList.toggle('is-active', on);
            p.hidden = !on;
          });
        });
      });
    });
  }

  /* --- Countdown --- */
  function initCountdown() {
    document.querySelectorAll('[data-cb-countdown]').forEach(function (root) {
      var raw = (root.dataset.cbEnd || '').trim();
      if (!raw) return;

      // "YYYY-MM-DD HH:MM" -> parse as local time (Safari rejects the space form)
      var parts = raw.replace('T', ' ').split(/[- :]/).map(Number);
      var end = new Date(
        parts[0], (parts[1] || 1) - 1, parts[2] || 1,
        parts[3] || 0, parts[4] || 0, 0
      );
      if (isNaN(end.getTime())) return;

      var clock = root.querySelector('[data-cb-clock]');
      var out = {
        d: root.querySelector('[data-cb-days]'),
        h: root.querySelector('[data-cb-hours]'),
        m: root.querySelector('[data-cb-mins]'),
        s: root.querySelector('[data-cb-secs]')
      };
      var pad = function (n) { return String(n).padStart(2, '0'); };
      var timer = null;

      function tick() {
        var diff = end.getTime() - Date.now();
        if (diff <= 0) {
          window.clearInterval(timer);
          if (clock) clock.textContent = root.dataset.cbExpiredText || '';
          return;
        }
        var s = Math.floor(diff / 1000);
        if (out.d) out.d.textContent = pad(Math.floor(s / 86400));
        if (out.h) out.h.textContent = pad(Math.floor((s % 86400) / 3600));
        if (out.m) out.m.textContent = pad(Math.floor((s % 3600) / 60));
        if (out.s) out.s.textContent = pad(s % 60);
      }

      tick();
      timer = window.setInterval(tick, 1000);
    });
  }

  /* --- Click to copy a discount code --- */
  function initCopy() {
    document.querySelectorAll('[data-cb-copy]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var code = btn.dataset.cbCopy;
        var label = btn.querySelector('[data-cb-copy-label]');
        var original = label ? label.textContent : '';

        function done() {
          if (!label) return;
          label.textContent = 'Copied';
          window.setTimeout(function () { label.textContent = original; }, 2000);
        }

        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(code).then(done).catch(done);
        } else {
          var tmp = document.createElement('textarea');
          tmp.value = code;
          tmp.style.position = 'fixed';
          tmp.style.opacity = '0';
          document.body.appendChild(tmp);
          tmp.select();
          try { document.execCommand('copy'); } catch (e) { /* no-op */ }
          document.body.removeChild(tmp);
          done();
        }
      });
    });
  }

  /* --- Featured product ---
     Gallery + variant matching + quantity + add-ons/upsell + add-to-cart
     for `.cb-fp` now all live in assets/cb-product.js instead: the section
     carries the same `data-cb-pdp` marker as the real product page, so
     cb-product.js's initPdp() boots on it automatically and the homepage
     Featured product block behaves exactly like the product's own page
     (this used to be a thinner, separate implementation here that only
     handled the gallery/quantity/legend — no price updates, no add-ons —
     which is what made the homepage version fall out of sync with the
     real product page). Kept as a no-op stub only so `initBatchTwo` below
     doesn't need to change shape. */
  function initFeaturedProduct() {}

  function initBatchTwo() {
    initTabs();
    initCountdown();
    initCopy();
    initFeaturedProduct();
  }

  initBatchTwo();
  document.addEventListener('shopify:section:load', initBatchTwo);
})();
